<template>
  <Dropdown
    :dropMenuList="getDropMenuList"
    :trigger="getTrigger"
    placement="bottom"
    overlayClassName="multiple-tabs__dropdown"
    @menu-event="handleMenuEvent"
  >
    <div :class="prefixCls" @contextmenu="handleContext" v-if="getIsTabs">
      <div @click="handleTabClick" class="w100b" style="text-align: center;">
        {{ displayTitle }}
      </div>
    </div>
    <span :class="`${prefixCls}__extra-quick`" v-else @click="handleContext">
      <Icon icon="ion:chevron-down" />
    </span>
  </Dropdown>
</template>
<script lang="ts">
  import type { PropType } from 'vue';
  import type { RouteLocationNormalized } from 'vue-router';

  import { defineComponent, computed, unref, ref, onMounted, onBeforeUnmount } from 'vue';
  import { Dropdown } from '@/components/Dropdown/index';
  import { Icon } from '@/components/Icon';

  import { TabContentProps } from '../types';

  import { useDesign } from '@/hooks/web/useDesign';
  import { useI18n } from 'vue-i18n';
  import { useTabDropdown } from '../useTabDropdown';
  import { StorageUtils } from 'xmgoing-utils';
  import eventBus from '@/events/eventBus';

  export default defineComponent({
    name: 'TabContent',
    components: { Dropdown, Icon },
    props: {
      tabItem: {
        type: Object as PropType<RouteLocationNormalized>,
        default: null,
      },
      isExtra: Boolean,
    },
    setup(props) {
      const { prefixCls } = useDesign('multiple-tabs-content');
      const { t } = useI18n();

      // 动态标签名称状态
      const dynamiceName = ref('');

      /**
       * 获取标签页要显示的标题
       * 优先级: meta 标题 > 按 path 缓存的菜单名 > session curMenuName > 动态名称
       */
      const getTitle = computed(() => {
        const { tabItem: { meta, path, fullPath } = {} as RouteLocationNormalized } = props;
        let title = meta && t(meta.title as string);

        // 处理特殊的动态标题情况：按 path 取标题，避免所有页签共用 curMenuName 导致全部变成「首页」
        if (title === 'dynamicName' || title === '页面') {
          title =
            (path && StorageUtils.getSessionItem(path)) ||
            (fullPath && StorageUtils.getSessionItem(fullPath)) ||
            StorageUtils.getSessionItem('curMenuName') ||
            '';
        }

        // 如果标题无效则返回动态名称
        if (title == null || title === 'undefined') {
          return dynamiceName.value;
        }

        return title || '';
      });



      /**
       * 组合静态和动态标题的计算属性
       */
      const displayTitle = computed(() => {
        return getTitle.value || dynamiceName.value || '';
      });

      /**
       * 处理标签页点击事件 - 更新 session 存储和文档标题
       */
      const handleTabClick = () => {
        const title = unref(displayTitle);
        if (title) {
          StorageUtils.addSessionItem('curMenuName', title);
          document.title = title;
        }
      };

      const getIsTabs = computed(() => !props.isExtra);

      const getTrigger = computed((): ('contextmenu' | 'click' | 'hover')[] =>
        unref(getIsTabs) ? ['contextmenu'] : ['click'],
      );

      const { getDropMenuList, handleMenuEvent, handleContextMenu } = useTabDropdown(
        props as TabContentProps,
        getIsTabs,
      );

      /**
       * 处理右键菜单事件
       */
      function handleContext(e: Event) {
        props.tabItem && handleContextMenu(props.tabItem)(e);
      }

      /**
       * 从事件总线设置动态标签名称
       */
      function setDynamicName(name: string) {
        if (unref(displayTitle)==null||unref(displayTitle)==='') {
          dynamiceName.value = name || '';
        }
      }

      // 注册事件监听器
      onMounted(() => {
        eventBus.off('setCurTabName', setDynamicName);
        eventBus.on('setCurTabName', setDynamicName);
      });

      // 清理事件监听器
      onBeforeUnmount(() => {
        eventBus.off('setCurTabName', setDynamicName);
      });

      return {
        prefixCls,
        displayTitle,
        handleTabClick,
        getDropMenuList,
        handleMenuEvent,
        handleContext,
        getTrigger,
        getIsTabs,
      };
    },
  });
</script>
